import { ArrowDropUp } from '@mui/icons-material';
import {
  persistIdentity,
  requestStoreReset,
  StoreIdentity,
} from 'components/JotaiStoreProvider/storeReset';
import Tooltip from 'components/Tooltip';
import { useAtom, useAtomValue } from 'jotai';
import { CompanyData } from 'lib/api/company/types';
import { cancelAllRequestsForCompany } from 'lib/callApi';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
// Load component styles without requiring TypeScript CSS module declarations.
require('./styles.css');
require('views/Admin/Dashboard/styles.css');
import {
  companyLogoAtom,
  companyDataAtom,
  companyIdAtom,
  currentCompanyAtom,
  currentDashboardAtom,
  useOnCompanyChangeResetAtoms,
} from 'views/state';

// Reset the url query string params on company change to avoid confusion as the
// data will be different for different companies.
function clearQueryParams(): void {
  const url = new URL(window.location.href);
  url.search = '';
  window.history.replaceState({}, '', url.toString());
}

interface Props {
  setCurrentCompany: (id) => void;
}

export interface CompanyOption {
  readonly value: number;
  readonly label: string;
}

const LogoCompaniesDropdown = ({ setCurrentCompany }: Props) => {
  const [companyId, setCompanyId] = useAtom(companyIdAtom);
  const [companyLogo, setCompanyLogo] = useAtom(companyLogoAtom);
  const [companyName, setCompanyName] = useAtom(currentCompanyAtom);
  const companiesData = useAtomValue(companyDataAtom);
  const dashboardType = useAtomValue(currentDashboardAtom);
  const [selectedCompanyOption, setSelectedCompanyOption] =
    useState<CompanyOption>();
  const [logoVisible, setLogoVisible] = useState<boolean>(true);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [dropdownEnabled, setDropdownEnabled] = useState(false);
  const resetOnCompanyChangeAtoms = useOnCompanyChangeResetAtoms();

  // Establish/resync the selected company on mount and after each store reset.
  // Runs with the freshly rehydrated companyId; never triggers the store reset
  // itself (applyMountedCompany is a no-op for the already-established company).
  useEffect(() => {
    if (companiesData?.length > 0) {
      // Fallback to the first available company when currentCompanyId is stale
      // (-1 on first login, or an id the user can no longer access).
      const currentCompany: CompanyData | undefined =
        companiesData.find((x) => x.id === companyId) ?? companiesData[0];

      if (currentCompany) {
        const { id, display_name, logo } = currentCompany;
        applyMountedCompany(id, display_name, logo);
        setSelectedCompanyOption(toCompanyOption({ value: id, display_name }));
      }

      // Enable the dropdown once companies are loaded (more than 1)
      // so the user can switch companies.
      if (companiesData?.length > 1) {
        setDropdownEnabled(true);
      }
    }
    // applyMountedCompany is a per-render closure; excluding it is intentional
    // (its same-company guard makes any redundant re-run a no-op). Re-establish
    // only when the roster or the active company changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companiesData, companyId]);

  const toCompanyOption = (dataItem: {
    value: number;
    display_name: string;
  }) => {
    return { value: dataItem.value, label: dataItem.display_name };
  };

  /**
   * Commit a company change: same-company guard → curated reset → abort old
   * requests → update the class shell → persist identity → clear URL query
   * params. Shared by the mount and user paths — they differ ONLY in how the
   * identity is written:
   *  - writeToStore=true (mount): identity atoms are written to the CURRENT
   *    store (and localStorage via write-through) so siblings read the new
   *    company immediately; no store reset follows.
   *  - writeToStore=false (user): identity is persisted to localStorage only
   *    (persistIdentity); the caller then requests a store reset and the fresh
   *    store is seeded from the returned identity — writing to the old store
   *    here would re-derive a duplicate dashboard request before the swap.
   * Reset runs BEFORE cancel so any old-company requests the reset re-derives
   * are aborted, not left dangling. Returns the identity for the fresh store in
   * BOTH paths; the mount wrapper discards it.
   */
  const commitCompanyChange = (
    id: number,
    display_name: string,
    logo: string,
    writeToStore: boolean,
  ): StoreIdentity | null => {
    const oldCompanyId = companyId;

    // Same-company selection — no-op.
    if (oldCompanyId === id) return null;

    resetOnCompanyChangeAtoms();

    // Abort old-company requests (including any re-derived by the reset above).
    cancelAllRequestsForCompany(oldCompanyId);

    // This is required for the class base component, once it is refactored in functional component
    // TO DO: remove this function dependency, after refactoring
    setCurrentCompany(id);

    if (writeToStore) {
      setCompanyId(id);
      setCompanyName(display_name);
      setCompanyLogo(logo);
      setLogoVisible(true);
      setIsHovered(false);
    } else {
      // Persist BEFORE the swap so the fresh store's requests carry the new id
      // (callApi reads currentCompanyId from localStorage at request time).
      persistIdentity(id, display_name, logo);
    }

    clearQueryParams();

    return { id, name: display_name, logo, dashboardType };
  };

  /**
   * Mount/effect path: establish the resolved company in the CURRENT store.
   * Runs on first load and after each store reset (stale-id fallback to the
   * first company); must never trigger the store reset itself.
   */
  const applyMountedCompany = (
    id: number,
    display_name: string,
    logo: string,
  ) => {
    commitCompanyChange(id, display_name, logo, true);
  };

  /**
   * User path: persist identity to localStorage (never writes identity to the
   * old store) and return the identity the fresh store must be seeded with, or
   * null on a same-company no-op. The caller triggers the store reset.
   */
  const switchCompany = (
    id: number,
    display_name: string,
    logo: string,
  ): StoreIdentity | null => commitCompanyChange(id, display_name, logo, false);

  const updateCompany = (e) => {
    setSelectedCompanyOption(e);
    const dataItem = companiesData.find((x) => x.id === e.value);
    if (!dataItem) return;
    // Store reset is user-action-only; the mount/effect path calls
    // applyMountedCompany but must never remount, so it ignores its result.
    const identity = switchCompany(
      dataItem.id,
      dataItem.display_name,
      dataItem.logo,
    );
    if (identity) requestStoreReset(identity);
  };

  const renderLogo = () => {
    const iconSlot = dropdownEnabled && (
      <ArrowDropUp className={`dropdown-icon ${isHovered ? 'active' : ''}`} />
    );

    if (
      companyLogo !== '' &&
      companyLogo !== null &&
      companyLogo !== undefined &&
      companyLogo.indexOf('assets/sp-default.png') === -1
    ) {
      return (
        <div
          className="d-flex align-items-center justify-content-center company-logo-container"
          onClick={() => setLogoVisible(false)}
        >
          <img
            style={{ borderRadius: '0' }}
            className="evisions_logo"
            src={companyLogo}
            alt={companyName + '-logo'}
          />
          {iconSlot}
        </div>
      );
    } else {
      return (
        <div
          className="d-flex align-items-center justify-content-center company-logo-container"
          onClick={() => setLogoVisible(false)}
        >
          <span>{companyName}</span>
          {iconSlot}
        </div>
      );
    }
  };

  const renderDropdown = () => {
    return (
      <div
        id="logo-dropdown"
        className="logo-dropdown-container px-2"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {logoVisible || !dropdownEnabled ? (
          renderLogo()
        ) : (
          <Select
            className={`custom-360-dropdown w-fit-content`}
            options={
              companiesData?.map((item) => {
                return toCompanyOption({
                  value: item.id,
                  display_name: item.display_name,
                });
              }) || []
            }
            value={selectedCompanyOption}
            onChange={updateCompany}
            styles={{
              control: (provided, state) => ({
                ...provided,
                background: 'transparent', // Make the background transparent
                border: state.isFocused ? '1px solid #ccc' : '1px solid #ccc', // Border color changes on focus
                borderRadius: '5px', // Rounded corners
                boxShadow: 'none', // Remove shadow
                color: '#fff', // Set text color to white
              }),
              dropdownIndicator: (provided, _state) => ({
                ...provided,
                color: '#fff', // Set arrow color to white when focused
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected
                  ? '#007bff'
                  : state.isFocused
                    ? '#007bff33'
                    : 'transparent',
                color: state.isSelected ? '#fff' : '#000',
                cursor: 'pointer',
              }),
              singleValue: (provided) => ({
                ...provided,
                color: '#fff', // Set text color to white for the selected value
              }),
              input: (provided) => ({
                ...provided,
                color: '#fff', // Set text color to white when typing
              }),
              placeholder: (provided) => ({
                ...provided,
                color: '#fff', // Set text color to white for the placeholder
              }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }), // Set z-index for the menu portal
            }}
            closeMenuOnSelect={true}
            menuPortalTarget={document.body}
          />
        )}
      </div>
    );
  };

  return dropdownEnabled ? (
    <div style={{ textTransform: 'none', fontVariant: 'none' }}>
      <Tooltip
        id="logo-dropdown"
        title={companyName}
        tooltip="Click to switch company"
      >
        {renderDropdown()}
      </Tooltip>
    </div>
  ) : (
    renderDropdown()
  );
};

export default LogoCompaniesDropdown;
